import React from 'react';
import { Chip } from '@mui/material';
import {
  Warning,
  LocationOn,
  Schedule,
  Store,
  Shield,
  ErrorOutline,
} from '@mui/icons-material';

/**
 * A single risk alert row.
 */
const RiskAlertRow = ({ icon, title, detail, severity }) => {
  const severityConfig = {
    high: { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-700', chip: 'error' },
    medium: { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', chip: 'warning' },
    low: { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700', chip: 'info' },
  };
  const config = severityConfig[severity] || severityConfig.low;

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl ${config.bg} ${config.border} border`}>
      <div className={`mt-0.5 ${config.text}`}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-gray-800 truncate">{title}</p>
          <Chip
            label={severity.toUpperCase()}
            color={config.chip}
            size="small"
            variant="filled"
            sx={{ fontWeight: 700, fontSize: '0.55rem', height: 20, minWidth: 50 }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
      </div>
    </div>
  );
};

/**
 * RiskAlertsPanel
 *
 * Displays categorized risk alerts:
 *   - High Exposure District
 *   - Overdue Harvest
 *   - Vendor Concentration Risk
 *   - Generic alerts from data
 */
const RiskAlertsPanel = ({ data = {}, loading = false }) => {
  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 animate-pulse min-h-[280px]">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-6" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-xl mb-3" />
        ))}
      </div>
    );
  }

  // Build alert list from data
  const alerts = [];

  // High-exposure districts
  if (data.highExposureDistricts?.length > 0) {
    data.highExposureDistricts.forEach((d) => {
      alerts.push({
        icon: <LocationOn />,
        title: `High Exposure: ${d.district}`,
        detail: `₹${(d.exposure / 100000).toFixed(1)}L exposure across ${d.farmerCount} farmers`,
        severity: d.exposure > 1000000 ? 'high' : 'medium',
      });
    });
  }

  // Overdue harvests
  if (data.overdueHarvests > 0) {
    alerts.push({
      icon: <Schedule />,
      title: `Overdue Harvest Warning`,
      detail: `${data.overdueHarvests} credit case${data.overdueHarvests !== 1 ? 's' : ''} with overdue harvest beyond expected cycle`,
      severity: data.overdueHarvests > 5 ? 'high' : 'medium',
    });
  }

  // Vendor concentration
  if (data.vendorConcentrationRisk?.length > 0) {
    data.vendorConcentrationRisk.forEach((v) => {
      alerts.push({
        icon: <Store />,
        title: `Vendor Concentration: ${v.vendorName}`,
        detail: `${v.percentage.toFixed(1)}% of total escrow payments go to this vendor`,
        severity: v.percentage > 40 ? 'high' : v.percentage > 25 ? 'medium' : 'low',
      });
    });
  }

  // Default risk
  if (data.defaultRiskCount > 0) {
    alerts.push({
      icon: <ErrorOutline />,
      title: 'Default Risk Detected',
      detail: `${data.defaultRiskCount} application${data.defaultRiskCount !== 1 ? 's' : ''} approaching default threshold`,
      severity: 'high',
    });
  }

  // NPA warning
  if (data.npaRiskPercent > 10) {
    alerts.push({
      icon: <Shield />,
      title: 'NPA Risk Elevated',
      detail: `Portfolio NPA risk at ${data.npaRiskPercent.toFixed(1)}% — exceeds 10% threshold`,
      severity: 'high',
    });
  }

  // If no alerts, show a healthy state
  if (alerts.length === 0) {
    alerts.push({
      icon: <Shield />,
      title: 'Portfolio Healthy',
      detail: 'No significant risk alerts detected at this time',
      severity: 'low',
    });
  }

  // Sort by severity: high → medium → low
  const severityOrder = { high: 0, medium: 1, low: 2 };
  alerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  const highCount = alerts.filter((a) => a.severity === 'high').length;
  const mediumCount = alerts.filter((a) => a.severity === 'medium').length;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-md transition-shadow duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <Warning sx={{ fontSize: 20, color: '#ef4444' }} />
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Risk Alerts</h3>
        </div>
        <div className="flex items-center gap-1">
          {highCount > 0 && (
            <Chip
              label={`${highCount} High`}
              color="error"
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
            />
          )}
          {mediumCount > 0 && (
            <Chip
              label={`${mediumCount} Medium`}
              color="warning"
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
            />
          )}
        </div>
      </div>

      {/* Alert list */}
      <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
        {alerts.map((alert, idx) => (
          <RiskAlertRow key={idx} {...alert} />
        ))}
      </div>
    </div>
  );
};

export default RiskAlertsPanel;
